// PaymentInfoPicker.jsx — "Payment Information" section for invoices and receipts.
// Pick a saved bank account and/or payment link to fill the form's fields (which stay
// editable), or save the current details for next time.
//
// Saved sources:
//   • src/paymentPresets.js — company-wide accounts/links (everyone sees them)
//   • "Save for next time"  — kept in this browser only (localStorage)
//
// Usage (inside a form whose state is `f` / `sf`):
//   <PaymentInfoPicker
//     bank={f.bankPaymentInfo}
//     link={f.paystackPaymentLink}
//     currency={f.currency}   // optional: lists matching-currency accounts first
//     onChange={({ bank, link }) => sf((p) => ({ ...p, bankPaymentInfo: bank, paystackPaymentLink: link }))}
//   />
import React, { useState } from "react";
import { BANK_ACCOUNTS, PAYMENT_LINKS } from "./paymentPresets";

const STORE_KEY = "crm_payment_presets";
const load = () => {
  try { const v = JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); return { banks: v.banks || [], links: v.links || [] }; }
  catch { return { banks: [], links: [] }; }
};
const save = (v) => { try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* storage unavailable */ } };

const SELECT = "w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]";
const bankText = (b) => [b.bankName, b.accountNumber, b.accountName].filter(Boolean).join(" · ");
const sameBank = (a, b) => a && b && a.accountNumber === b.accountNumber && a.bankName === b.bankName;

export default function PaymentInfoPicker({ bank, link, onChange, currency }) {
  const [local, setLocal] = useState(load);
  const [naming, setNaming] = useState(false);
  const [label, setLabel] = useState("");

  // Accounts in the document's currency first (stable order otherwise).
  const matches = (b) => !currency || !b.currency || b.currency === currency;
  const banks = [
    ...BANK_ACCOUNTS.map((b) => ({ ...b, source: "company" })),
    ...local.banks.map((b) => ({ ...b, source: "local" })),
  ].sort((a, b) => Number(matches(b)) - Number(matches(a)));
  const links = [
    ...PAYMENT_LINKS.map((l) => ({ ...l, source: "company" })),
    ...local.links.map((l) => ({ ...l, source: "local" })),
  ];
  const cur = { accountNumber: "", accountName: "", bankName: "", ...(bank || {}) };
  const bankIdx = banks.findIndex((b) => sameBank(b, cur));
  const linkIdx = links.findIndex((l) => l.url && l.url === link);

  const pickBank = (i) => {
    if (i === "") return;
    const b = banks[+i];
    onChange({ bank: { accountNumber: b.accountNumber || "", accountName: b.accountName || "", bankName: b.bankName || "" }, link });
  };
  const pickLink = (i) => { if (i !== "") onChange({ bank: cur, link: links[+i].url }); };

  const canSave = (cur.accountNumber && bankIdx === -1) || (link && linkIdx === -1);
  const saveCurrent = () => {
    const name = label.trim();
    if (!name) return;
    const next = { banks: [...local.banks], links: [...local.links] };
    if (cur.accountNumber && bankIdx === -1) next.banks.push({ label: name, ...cur });
    if (link && linkIdx === -1) next.links.push({ label: name, url: link });
    save(next); setLocal(next); setNaming(false); setLabel("");
  };
  const forget = (kind, item) => {
    const next = { ...local, [kind]: local[kind].filter((x) => (kind === "banks" ? !sameBank(x, item) : x.url !== item.url)) };
    save(next); setLocal(next);
  };

  const Option = ({ item, i, text }) => (
    <option value={i}>
      {item.label}{text ? ` — ${text}` : ""}
      {currency && item.currency && item.currency !== currency ? ` (${item.currency} account)` : ""}
      {item.source === "local" ? " (saved on this device)" : ""}
    </option>
  );
  const picked = banks[bankIdx];
  const currencyMismatch = currency && picked?.currency && picked.currency !== currency;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Payment Information</p>
        <span className="text-[10px] text-slate-400">Pick saved details — you can still edit them below</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Bank account</label>
          <select className={SELECT} value={bankIdx === -1 ? "" : bankIdx} onChange={(e) => pickBank(e.target.value)}>
            <option value="">{banks.length ? (cur.accountNumber ? "Custom (typed below)" : "Select an account…") : "No saved accounts yet"}</option>
            {banks.map((b, i) => <Option key={i} item={b} i={i} text={bankText(b)} />)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Payment link</label>
          <select className={SELECT} value={linkIdx === -1 ? "" : linkIdx} onChange={(e) => pickLink(e.target.value)}>
            <option value="">{links.length ? (link ? "Custom (typed below)" : "Select a link…") : "No saved links yet"}</option>
            {links.map((l, i) => <Option key={i} item={l} i={i} />)}
          </select>
        </div>
      </div>

      {currencyMismatch && (
        <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
          This is a {picked.currency} account, but this document is in {currency}. Double-check before sending.
        </p>
      )}

      {/* Remove a device-saved entry that's currently selected */}
      {(banks[bankIdx]?.source === "local" || links[linkIdx]?.source === "local") && (
        <div className="flex gap-3 text-[11px]">
          {banks[bankIdx]?.source === "local" && (
            <button type="button" onClick={() => forget("banks", banks[bankIdx])} className="text-slate-400 hover:text-rose-500 font-semibold">
              ✕ Forget this account
            </button>
          )}
          {links[linkIdx]?.source === "local" && (
            <button type="button" onClick={() => forget("links", links[linkIdx])} className="text-slate-400 hover:text-rose-500 font-semibold">
              ✕ Forget this link
            </button>
          )}
        </div>
      )}

      {canSave && !naming && (
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="text-[11px] font-bold text-[rgb(var(--brand-rgb))] hover:underline"
        >
          ＋ Save these details for next time
        </button>
      )}
      {naming && (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveCurrent(); } if (e.key === "Escape") setNaming(false); }}
            placeholder='Name it, e.g. "Zenith NGN account"'
            className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-[rgb(var(--brand-rgb))]"
          />
          <button type="button" onClick={saveCurrent} disabled={!label.trim()}
            className="text-[11px] font-bold px-3 py-1.5 rounded-lg text-white bg-[rgb(var(--brand-rgb))] disabled:opacity-40">
            Save
          </button>
          <button type="button" onClick={() => setNaming(false)} className="text-[11px] font-bold text-slate-400">Cancel</button>
        </div>
      )}
    </div>
  );
}
